import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, FileText, Upload } from 'lucide-react';

const blank = { result: '', update_immediately: '', objective_evidence: '', certificates: '', correction_plan: '', comment: '', attachments: [] };

export function EvaluateDialog({ data, onClose, onSaved }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');

  useEffect(() => {
    if (data) {
      setForm({
        result: data.result || '',
        update_immediately: data.update_immediately == null ? '' : (data.update_immediately ? 'yes' : 'no'),
        objective_evidence: data.objective_evidence || '',
        certificates: data.certificates || '',
        correction_plan: data.correction_plan || '',
        comment: data.supervisor_comment || '',
        attachments: data.attachments || [],
      });
    }
  }, [data]);

  if (!data) return null;

  const upload = async (category, fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(category);
    try {
      for (const file of Array.from(fileList)) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
        const fd = new FormData(); fd.append('file', file);
        const res = await hrAPI.uploadEvalAttachment(data.sid, data.empId, category, fd);
        setForm(f => ({ ...f, attachments: [...f.attachments, res.data] }));
      }
      toast.success('File(s) attached');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(''); }
  };

  const removeAtt = async (att) => {
    if (!window.confirm(`Remove ${att.filename}?`)) return;
    try {
      await hrAPI.deleteEvalAttachment(data.sid, data.empId, att.id);
      setForm(f => ({ ...f, attachments: f.attachments.filter(a => a.id !== att.id) }));
      toast.success('Removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewAtt = async (att) => {
    try {
      const res = await hrAPI.evalAttachment(data.sid, data.empId, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to open'); }
  };

  const save = async () => {
    if (!form.result) { toast.error('Please select the training result'); return; }
    if (!form.comment.trim()) { toast.error('Supervisor comment is mandatory'); return; }
    setSaving(true);
    try {
      await hrAPI.sessionFeedback(data.sid, {
        employee_id: data.empId,
        supervisor_comment: form.comment,
        result: form.result || null,
        update_immediately: form.update_immediately === '' ? null : form.update_immediately === 'yes',
        objective_evidence: form.objective_evidence,
        certificates: form.certificates,
        correction_plan: form.correction_plan,
      });
      toast.success('Evaluation saved');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const AttList = ({ category }) => {
    const items = form.attachments.filter(a => a.category === category);
    return (
      <div className="mt-1.5 space-y-1">
        {items.map(a => (
          <div key={a.id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`eval-att-${a.id}`}>
            <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <button className="truncate text-slate-700 hover:text-pink-700 hover:underline flex-1 text-left" onClick={() => viewAtt(a)}>{a.filename}</button>
            <button onClick={() => removeAtt(a)} className="text-slate-400 hover:text-red-600" data-testid={`eval-att-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <label className="inline-flex items-center gap-1.5 text-xs text-pink-700 cursor-pointer hover:underline">
          <Upload className="w-3.5 h-3.5" /> {uploading === category ? 'Uploading...' : 'Attach file(s)'}
          <input type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
            onChange={e => { upload(category, e.target.files); e.target.value = ''; }} data-testid={`eval-upload-${category}`} />
        </label>
      </div>
    );
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hr-feedback-dialog">
        <DialogHeader><DialogTitle>Supervisor Evaluation — {data.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Result of Employee's Training *</Label>
            <Select value={form.result || undefined} onValueChange={v => setForm({ ...form, result: v })}>
              <SelectTrigger data-testid="hr-feedback-result"><SelectValue placeholder="Select result" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="satisfactory">Satisfactory Attendance / Completion</SelectItem>
                <SelectItem value="non_satisfactory">Non-Satisfactory / Incomplete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">If skill matrix update needed, update immediately after training?</Label>
            <Select value={form.update_immediately || undefined} onValueChange={v => setForm({ ...form, update_immediately: v })}>
              <SelectTrigger data-testid="hr-feedback-immediate"><SelectValue placeholder="Yes / No" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Objective evidence to be collected</Label>
            <Textarea rows={2} value={form.objective_evidence} onChange={e => setForm({ ...form, objective_evidence: e.target.value })} placeholder="Details of objective evidence" data-testid="hr-feedback-evidence" />
            <AttList category="evidence" />
          </div>
          <div>
            <Label className="text-xs">Certificates / Credentials Obtained</Label>
            <Input value={form.certificates} onChange={e => setForm({ ...form, certificates: e.target.value })} placeholder="If applicable" data-testid="hr-feedback-certs" />
            <AttList category="certificate" />
          </div>
          {form.result === 'non_satisfactory' && (
            <div>
              <Label className="text-xs">Correction plan (re-take / alternate training / other action)</Label>
              <Textarea rows={2} value={form.correction_plan} onChange={e => setForm({ ...form, correction_plan: e.target.value })} placeholder="Corrective action" data-testid="hr-feedback-correction" />
            </div>
          )}
          <div>
            <Label className="text-xs">Immediate supervisor comment on effectiveness (mandatory) *</Label>
            <Textarea rows={3} value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} placeholder="Comment on effectiveness of the training" data-testid="hr-feedback-text" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-feedback-save">{saving ? 'Saving...' : 'Save Evaluation'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
